// Suggesting agents as they type, from the directory the page already has, matched here. What starts a suggestion:
// "@" and the start of a name; a whole first name or surname, or four letters that start one; or a first name and
// the start of a surname ("Jane Sm"). A short everyday word ("the") suggests no one.

const MOST_AGENTS = 6;
// Letters of one word that may start a name: fewer would catch everyday words.
const SHORTEST_START = 4;

const wordsOf = (text) =>
  String(text ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9'\s-]/g, " ")
    .split(/\s+/)
    .filter(Boolean);

// What the end of the text is naming: "@" and what follows it, or the word being typed and the one before it.
const nameBeingTyped = (text) => {
  const value = String(text ?? "");
  const at = value.match(/@([^\s@]*)$/);
  if (at) return { term: wordsOf(at[1])[0] || "", from: at.index, isAt: true };
  const last = value.match(/(?:^|\s)(\S+)$/);
  if (!last) return null;
  const from = last.index + last[0].indexOf(last[1]);
  const before = value.slice(0, from).match(/(?:^|\s)(\S+)\s+$/);
  return {
    term: wordsOf(last[1])[0] || "",
    from,
    before: before && { word: wordsOf(before[1])[0] || "", from: before.index + before[0].indexOf(before[1]) },
  };
};

// Whether the words being typed start this name: a first name and the start of the surname, or one word of it.
const startsName = (typing, name, words = wordsOf(name)) => {
  if (typing.isAt) return !typing.term || words.some((word) => word.startsWith(typing.term));
  if (typing.before && words.length > 1 && words[0] === typing.before.word && words[1].startsWith(typing.term)) return true;
  return (
    typing.term.length >= 3 &&
    words.some((word) => word === typing.term || (typing.term.length >= SHORTEST_START && word.startsWith(typing.term)))
  );
};

// Where a picked name goes: over the word before too when it is this name's first name ("Jane Smi" → "Jane Smith").
const replaceFromFor = (typing, name) => {
  const words = wordsOf(name);
  return typing.before && words.length > 1 && words[0] === typing.before.word && words[1].startsWith(typing.term)
    ? typing.before.from
    : typing.from;
};

// A name already written out in full at the end of the text is finished: it is not suggested again.
const endsWithName = (text, name) => {
  const words = wordsOf(name);
  return words.length > 1 && wordsOf(text).join(" ").endsWith(words.join(" "));
};

// The agents the end of `text` is starting to name, up to six, each as { _id, name, email, phone, picture, tag (their
// network), kind, replaceFrom }.
export const suggestPeople = (text, { agents = [] }) => {
  const typing = nameBeingTyped(text);
  if (!typing) return [];
  return agents
    .filter((agent) => agent?.name && !endsWithName(text, agent.name) && startsName(typing, agent.name))
    .slice(0, MOST_AGENTS)
    .map((agent) => ({
      _id: agent._id,
      name: agent.name,
      email: agent.email,
      phone: agent.phone || agent.mobile,
      picture: agent.picture,
      tag: agent.networkName,
      kind: "agent",
      replaceFrom: replaceFromFor(typing, agent.name),
    }));
};

// The text with the words that were naming the person replaced by their full name.
export const withPerson = (text, suggestion) => `${String(text).slice(0, suggestion.replaceFrom)}${suggestion.name} `;
